import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { VoiceCallState } from '../lib/use-voice-call';
import { useProductName } from '../lib/use-brand';
import { useTranslation } from '../lib/i18n';
import { centerInline, clampInlineEnd, windowViewport } from '../lib/popover-placement';
import { VoiceAudioSettings } from './symphony/VoiceAudioSettings';
import { Button, Card, IconButton, ScrollArea } from './symphony/ui';
import voiceHeaderBtn from './symphony/voiceHeaderButton.module.css';
import { ChevronDown, CircleAlert, Info, Maximize2, Mic, MicOff, Minimize2, X } from 'lucide-react';
import { formatElapsed } from '../lib/voice-call-timer';
import { messageDir } from '../lib/message-direction';
import { useGenericBi } from '../lib/use-generic-bi';
import { CHAT_HOME_BI } from './symphony/chat-surface/message-utils';
import { useActiveConversation } from './chat/ActiveConversationContext';
import { useNarrow } from '../lib/use-narrow';

// The call surface's orb colour story. Copy is localized below; only the visual tokens live here.
const VOICE_COPY = {
  // The orb's resting fill before the tick loop's first frame runs — matches the "listening/idle"
  // colour the loop itself uses (see the tick loop below), so there's no flash of a different shade.
  orbFill: 'var(--ds-lime-500)',
  // The wait ring is progress, not branding — it has to read against the white card.
  ringColor: 'var(--ds-control-fill-on-default)',
} as const;

// Wait-ring pacing. ask_orion answers take ~11s on average (p90 ~29s), so rather
// than a linear bar that would sit full half the time, the ring fills on an
// exponential ease-out (1 - e^(-t/τ)): ~78% by the 11s median, decelerating
// forever and never reaching 100% on its own — the real answer snaps it shut.
const RING_TAU_MS = 7500;

// The deaf-call hint. Quiet rather than alarming: it is a suggestion about hardware, not an
// error, and it sits on a call the user is still trying to have.
const SILENCE_HINT: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  maxWidth: 'min(90vw, 420px)',
  padding: '8px 10px 8px 12px',
  borderRadius: 'var(--sym-radius-card)',
  background: 'var(--sym-bg-elevated)',
  border: '1px solid rgba(var(--hairline-rgb),0.18)',
  color: 'rgba(var(--ink-rgb),0.75)',
  fontSize: 13,
  lineHeight: 1.4,
  textAlign: 'start',
};
const RING_COMPLETE_MS = 320; // ease the remaining arc closed once the answer lands
const RING_FADE_MS = 260; // then fade the ring away
// "About Voice mode" info card width (Figma 14626:190420). Used both to render the card and to
// clamp its centered position so it can't overflow the call panel on a narrow surface.
const INFO_CARD_WIDTH = 300;
// Gap from the header (top bar) bottom to the card top. Figma 14701:107274: header row bottom 64 →
// card top 82 = 18. Anchored to the header rather than the info button, since the card is centered
// over the panel (not hung off the button) and the button sits centered within the taller header row.
const INFO_CARD_GAP = 18;

/**
 * Full-screen call surface. The point is feedback symmetry in a noisy room:
 * the mic bars prove Orion hears you (and how loudly), the orb proves Orion
 * is the one talking, and the status line says which way the channel is
 * flowing right now. Live captions ride underneath.
 *
 * All level-driven motion (orb glow, mic bars, status line) is driven by a
 * single rAF loop writing to DOM refs — polling `getAudioState()` — so the
 * page never re-renders at audio rate. React only re-renders for status,
 * caption, and mute changes.
 */
export function VoiceCallOverlay({ voice, contained = false }: { voice: VoiceCallState; contained?: boolean }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const copy = {
    ...VOICE_COPY,
    name: productName,
    calling: t('voiceCall.calling', { productName }),
    speaking: t('voiceCall.speaking', { productName }),
    working: t('voiceCall.workingOnIt', { productName }),
    mutedLabel: t('voiceCall.muted'),
    listeningHeard: t('voiceCall.listeningHeard'),
    listening: t('voiceCall.listening'),
    // Bottom-control labels (Figma 12835:79206). The visible "Mute" label stays put
    // whether or not the mic is muted — only the button skin flips — so the aria
    // label carries the toggle direction.
    muteButton: t('voiceCall.mute'),
    unmuteAria: t('voiceCall.unmute'),
    end: t('voiceCall.end'),
    endCall: t('voiceCall.endCall'),
    dismiss: t('voiceCall.dismiss'),
  };
  const { logPageClickBi } = useGenericBi('voice-call');
  // Controlled so the "we can't hear you" hint can open the panel it points at, rather than
  // telling the user to go and find it.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The voice-mode header's Info popover (Figma "Voice mode Default" 14701:127070).
  const [infoOpen, setInfoOpen] = useState(false);
  const topBarRef = useRef<HTMLDivElement | null>(null);
  const infoTriggerRef = useRef<HTMLDivElement | null>(null);
  const [infoPos, setInfoPos] = useState<{ top: number; left: number; right?: undefined } | { top: number; right: number; left?: undefined } | null>(null);
  // The ↗ expand toggles the chat surface to fullscreen and back — the same chat-mode moves the
  // ChatHeader makes; on a phone every chat is already full, so it's wide-only.
  const { chatMode, setChatMode } = useActiveConversation();
  const narrow = useNarrow();
  const isFullscreen = !narrow && chatMode === 'fullscreen';
  // The Info popover drops from just below the header. On the narrow surfaces (mobile-web + desktop
  // side-chat) it's CENTERED over the call panel (Figma 14626:189496 / 14701:107274) — portalled to
  // <body> in fixed coords, so we center on the TOP BAR (which spans the panel, inset 16 each side)
  // rather than the whole viewport, and centering is direction-agnostic so it can't RTL-clip off the
  // opposite edge (a literal `right` used to). But in FULLSCREEN the panel is the whole (very wide)
  // window, where centering would strand the card mid-screen — there it right-aligns to the info button
  // instead (Figma 14626:190539), the way a normal top-right popover hangs off its trigger.
  useLayoutEffect(() => {
    if (!infoOpen) return undefined;
    const place = () => {
      const bar = topBarRef.current;
      if (!bar) return;
      const barRect = bar.getBoundingClientRect();
      const viewport = windowViewport();
      const top = barRect.bottom + INFO_CARD_GAP;
      if (isFullscreen) {
        const trig = infoTriggerRef.current;
        if (!trig) return;
        const r = trig.getBoundingClientRect();
        setInfoPos({ top, right: clampInlineEnd(viewport.width - r.right, INFO_CARD_WIDTH, viewport, 8) });
      } else {
        setInfoPos({ top, left: centerInline({ start: barRect.left, end: barRect.right }, INFO_CARD_WIDTH, viewport, 8) });
      }
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [infoOpen, isFullscreen]);
  const orbRef = useRef<HTMLDivElement | null>(null);
  // The two halo layers behind the orb (native `VoiceOrbMotion`'s "mid"/"outer") — see the tick
  // loop below for the per-frame scale/opacity math.
  const midHaloRef = useRef<HTMLDivElement | null>(null);
  const outerHaloRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Wait-ring: the progress arc, its <svg> wrapper (faded in/out), and the
  // animation clock. Start/done timestamps survive effect re-runs so a fill in
  // progress isn't reset when an unrelated dep (thinkingStep, mute) changes.
  const ringRef = useRef<SVGCircleElement | null>(null);
  const ringWrapRef = useRef<SVGSVGElement | null>(null);
  const ringStartRef = useRef<number | null>(null);
  const ringDoneAtRef = useRef<number | null>(null);
  const ringDoneFromRef = useRef(0);

  const orbSize = contained ? 84 : 120;
  // Hug the orb's rim (small gap) so the ring reads as part of the orb, not a
  // second circle floating in its glow.
  const ringSize = orbSize + 16;
  const ringR = ringSize / 2 - 5;
  // The halo behind the orb, sized to Figma's own ratio (88px orb inside a 192px halo).
  const haloSize = Math.round(orbSize * (192 / 88));
  const ringC = 2 * Math.PI * ringR;

  // Mount the overlay into document.body via portal so no ancestor's
  // `filter` / `transform` / `backdrop-filter` establishes a containing
  // block that would clip `position: fixed`. Specifically the chat
  // section sets backdropFilter once a conversation exists, and lazy-
  // creating one when voice starts would otherwise pin the overlay
  // inside the chat pane (sidebar visible alongside, off-center). This
  // path is currently unreachable in production (every real call goes
  // through `contained` — see components/symphony/chat-surface/MessagesPanel/
  // index.tsx) but is kept for a future non-contained surface.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setPortalTarget(document.body);
  }, []);

  // A live caption grows at its END, so once it outgrows its area the newest words are the ones worth
  // showing — a caption frozen on its opening words while the speaker moved on is the same "text is
  // cut" complaint in a different costume.
  const captionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [voice.caption?.text]);

  const active = voice.status === 'connecting' || voice.status === 'live';
  const { getAudioState, status, muted, working, thinkingStep } = voice;

  // The rAF loop below reads these render-varying values through a ref, so it is
  // NEVER torn down and recreated just because a caption streamed or the call
  // clock ticked. Rebuilding the effect reset the orb's smoothing envelope
  // (`orbSmooth` / `orbSmoothSlow`) to zero — and the overlay re-renders most
  // often exactly WHILE the user is speaking (that is when live captions arrive),
  // so every word knocked the circle back to its resting size faster than the
  // voice could grow it, and the slow outer halo never accumulated at all. The
  // orb read as unreactive. Running the loop once per call lets the level build
  // across frames, so the circle grows with the user's voice as intended.
  const liveRef = useRef({ status, muted, working, thinkingStep, copy });
  liveRef.current = { status, muted, working, thinkingStep, copy };

  useEffect(() => {
    if (!active) return;
    // Reconcile the wait-ring clock on `working` edges only — its fill must
    // survive unrelated re-renders (caption, thinkingStep, mute).
    const reconcileAt = performance.now();
    if (working) {
      // (Re)start filling when idle, or if a previous completion is still fading.
      if (ringStartRef.current == null || ringDoneAtRef.current != null) {
        ringStartRef.current = reconcileAt;
        ringDoneAtRef.current = null;
      }
    } else if (ringStartRef.current != null && ringDoneAtRef.current == null) {
      // Answer arrived: snap the remaining arc closed from wherever it had crept.
      const elapsed = reconcileAt - ringStartRef.current;
      ringDoneFromRef.current = 1 - Math.exp(-elapsed / RING_TAU_MS);
      ringDoneAtRef.current = reconcileAt;
    }
  }, [active, working]);

  useEffect(() => {
    if (!active) return;
    let frame = 0;
    let smoothedInput = 0;
    let smoothedOutput = 0;
    // Orb motion — ported verbatim from the native apps' `VoiceOrbMotion` (iOS
    // `VoiceCallScreen.swift`, Android `VoiceCallScreen.kt`), which is itself a 1:1 port of the
    // reference `Voice Screen.html`. It's a hand-rolled sine "breathe" plus a fast-attack/slow-
    // release envelope over the mic/speaker level — not a spring or a CSS keyframe loop — so the
    // three surfaces stay visually identical. Do not retune these constants independently of native.
    let orbSmooth = 0;
    let orbSmoothSlow = 0;
    let orbT = 0;
    let lastFrameMs: number | null = null;
    const tick = (now: number) => {
      const { status, muted, working, thinkingStep, copy } = liveRef.current;
      const audio = getAudioState();
      // Lerp toward the 8Hz level samples so 60fps motion stays fluid.
      smoothedInput += (audio.inputLevel - smoothedInput) * 0.25;
      smoothedOutput += (audio.outputLevel - smoothedOutput) * 0.25;

      // Frame-rate-independent envelope: native converts its per-60fps-frame lerp constants via
      // `1 - (1-perFrame)^steps` where `steps = dt*60` — same here. Clamp dt so a tab coming back
      // from background doesn't slam the envelope in one giant step.
      const dtSec = lastFrameMs == null ? 1 / 60 : Math.max(0, Math.min(0.1, (now - lastFrameMs) / 1000));
      lastFrameMs = now;
      const steps = dtSec * 60;
      const raw = muted ? 0 : Math.min(1, (audio.orionSpeaking ? audio.outputLevel : audio.inputLevel) * 6);
      const attackOrRelease = raw > orbSmooth ? 0.35 : 0.09;
      orbSmooth += (raw - orbSmooth) * (1 - Math.pow(1 - attackOrRelease, steps));
      orbSmoothSlow += (orbSmooth - orbSmoothSlow) * (1 - Math.pow(1 - 0.05, steps));

      // `orbT` accumulates real elapsed time from 0, same as native's `VoiceOrbMotion.t` — not
      // `now` itself, which would anchor the breathe phase to page-load time instead of call-start.
      orbT += dtSec;
      const breathe = Math.sin(orbT * 1.15) * 0.012;
      const coreScale = 1 + breathe + orbSmooth * 0.42;
      const midScale = 1 - breathe * 0.6 + orbSmooth * 0.16;
      const outerScale = 1 + orbSmoothSlow * 0.55 + orbSmooth * 0.1;
      const midAlpha = Math.min(1, 0.3 * (0.55 + orbSmooth * 0.45));
      const outerAlpha = Math.min(1, 0.14 * (0.25 + orbSmoothSlow * 0.75));
      // Vivid lime while listening/idle, the accent green while the assistant is actually
      // speaking — same two colours and same trigger as native.
      const rgbVar = audio.orionSpeaking ? 'var(--ds-fill-accent-brand-default-rgb)' : 'var(--ds-lime-500-rgb)';

      const orb = orbRef.current;
      if (orb) {
        orb.style.transform = `scale(${coreScale.toFixed(3)})`;
        orb.style.background = audio.orionSpeaking ? 'var(--ds-fill-accent-brand-default)' : 'var(--ds-lime-500)';
      }
      const midHalo = midHaloRef.current;
      if (midHalo) {
        midHalo.style.transform = `scale(${midScale.toFixed(3)})`;
        midHalo.style.background = `rgba(${rgbVar}, ${midAlpha.toFixed(3)})`;
      }
      const outerHalo = outerHaloRef.current;
      if (outerHalo) {
        outerHalo.style.transform = `scale(${outerScale.toFixed(3)})`;
        outerHalo.style.background = `rgba(${rgbVar}, ${outerAlpha.toFixed(3)})`;
      }

      const heard = smoothedInput > 0.015;
      const statusEl = statusRef.current;
      if (statusEl) {
        // This line is USUALLY our own localized copy, which must follow the page language. In exactly
        // one state it is the assistant's own streamed thinking step — LLM text, in whatever language
        // the call is in — and that takes its direction from its own script (Golden Rule 13). Anything
        // else keeps the page's direction; overriding a localized string would be the same bug pointing
        // the other way.
        const agentAuthored = status !== 'connecting' && working && !!thinkingStep?.trim();
        statusEl.textContent =
          status === 'connecting'
            ? copy.calling
            : working
              ? thinkingStep || copy.working
              : audio.orionSpeaking
                ? copy.speaking
                : muted
                  ? copy.mutedLabel
                  : heard
                    ? copy.listeningHeard
                    : copy.listening;
        // `''` hands the element back to its inherited direction rather than pinning it.
        statusEl.dir = agentAuthored ? messageDir(statusEl.textContent) : '';
      }

      barRefs.current.forEach((bar, i) => {
        if (!bar) return;
        const wobble = 0.6 + 0.4 * Math.sin(now / 90 + i * 1.7);
        const height = 4 + Math.min(40, smoothedInput * 320 * wobble);
        // scaleY instead of height — compositor-only, no layout reflow per frame.
        bar.style.transform = `scaleY(${(height / 44).toFixed(3)})`;
        bar.style.opacity = muted ? '0.25' : String(0.5 + Math.min(0.5, smoothedInput * 6));
      });

      const ring = ringRef.current;
      const wrap = ringWrapRef.current;
      if (ring && wrap) {
        if (ringStartRef.current != null && ringDoneAtRef.current == null) {
          // Filling: exponential ease-out toward (but never reaching) full.
          const elapsed = now - ringStartRef.current;
          const p = 1 - Math.exp(-elapsed / RING_TAU_MS);
          ring.style.strokeDashoffset = String(ringC * (1 - p));
          wrap.style.opacity = String(Math.min(1, elapsed / 240));
        } else if (ringDoneAtRef.current != null) {
          // Completing: ease the remainder closed, hold full briefly, then fade.
          const ct = now - ringDoneAtRef.current;
          const k = Math.min(1, ct / RING_COMPLETE_MS);
          const p = ringDoneFromRef.current + (1 - ringDoneFromRef.current) * (1 - Math.pow(1 - k, 3));
          ring.style.strokeDashoffset = String(ringC * (1 - p));
          wrap.style.opacity = String(
            ct <= RING_COMPLETE_MS ? 1 : Math.max(0, 1 - (ct - RING_COMPLETE_MS) / RING_FADE_MS),
          );
          if (ct > RING_COMPLETE_MS + RING_FADE_MS) {
            ringStartRef.current = null;
            ringDoneAtRef.current = null;
          }
        } else {
          wrap.style.opacity = '0';
        }
      }

      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // Deliberately depends only on STABLE values so the loop lives for the whole
    // call. The render-varying status/mute/working/copy are read through liveRef
    // inside `tick` — see the note on liveRef above for why rebuilding this effect
    // per render broke the orb's reaction to the user's voice.
  }, [active, getAudioState, ringC]);

  // `surfaceVisible` is the single gate: no call (idle/ended), or a live call the
  // user has shrunk to the island — in which case the session is still running and
  // <VoiceCallIsland> is what's on screen. A FAILURE is deliberately still shown
  // here, because the error screen owns the only dismiss.
  if (!voice.timing.surfaceVisible) return null;
  // Contained (floating panel) renders in place — no portal — and fills the
  // panel via `position: absolute` against the nearest positioned ancestor
  // (the chat section). The full-screen variant portals to <body> so no
  // ancestor's filter / transform / backdrop-filter clips its `position: fixed`.
  if (!contained && !portalTarget) return null;

  const failed = voice.status === 'error';
  // A refused or absent microphone is the most likely failure and the only recoverable one, so it
  // gets its own actionable words. EVERY other failure shows one general, human message — never the
  // raw provider error code ("Call failed — NotAllowedError" is technically accurate and useless: it
  // neither says what happened nor what to do). The raw `errorDetail` still rides the BI/Panorama
  // beacons for us to debug; the user just sees "couldn't connect, try again".
  const errorText = failed
    ? voice.errorDetail === 'mic-denied'
      ? t('voiceCall.micDenied')
      : voice.errorDetail === 'mic-missing'
        ? t('voiceCall.micMissing')
        : t('voiceCall.callFailed')
    : null;
  // A blocked mic keeps the call chrome (the orb + Mute/End controls, Figma "Mic- no access"
  // 14511:136721) rather than collapsing to a lone Dismiss — the only failure the user can fix
  // in place and continue from. Every other failure keeps the terminal Dismiss.
  const micDenied = failed && voice.errorDetail === 'mic-denied';

  // Top bar: shrink the call away, and the call clock. The clock is not decoration — the server
  // caps a call (MAX_CALL_DURATION_MS in lib/voice-quota.ts) and used to simply stop one at the
  // ceiling, which from the user's side is the assistant going silent mid-topic for no stated
  // reason. Seeing the time run is what makes the "ending soon" notice below legible rather than
  // alarming. Shared between both presentations — it's a thin strip pinned to the top of whichever
  // positioned ancestor renders it, so it never overlaps the composer.
  const topBar = (active || micDenied) && (
    <div
      ref={topBarRef}
      style={{
        position: 'absolute',
        top: contained ? 12 : 20,
        insetInlineStart: contained ? 16 : 20,
        insetInlineEnd: contained ? 16 : 20,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1,
      }}
    >
      {/* The call's own header (Figma 12835:79206). Contained: a white round "collapse to island"
          button on the left — the normal ChatHeader is hidden while a call is up (see FloatingChat),
          so this IS the header. */}
      <IconButton
        variant={contained ? 'outlined' : 'text'}
        color="standard"
        size="medium"
        style={contained ? { width: 40, height: 40, borderRadius: '50%', background: 'var(--ds-surface-sunken)', borderColor: 'transparent' } : undefined}
        aria-label={t('voiceCall.minimize')}
        data-testid="voice-call-minimize"
        onClick={() => {
          logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_minimize', location: 'voice_call' });
          voice.minimize();
        }}
      >
        <ChevronDown size={18} aria-hidden />
      </IconButton>
      {/* The full-screen variant keeps the running clock; the contained header matches the Figma,
          which shows no clock (the ending-soon notice still surfaces in the centre column). */}
      {!contained && (
        <div
          data-testid="voice-call-elapsed"
          aria-label={t('voiceCall.duration')}
          style={{
            fontSize: 14,
            // Tabular figures so the running clock doesn't jitter every second.
            fontVariantNumeric: 'tabular-nums',
            color: 'rgba(var(--ink-rgb),0.65)',
          }}
        >
          {formatElapsed(voice.timing.elapsedMs)}
        </div>
      )}
      {/* The voice-mode header's top-right cluster (Figma "Voice mode Default" 14701:127070):
          info ⓘ · settings ⚙ · expand ↗. Settings is the same volume/device/test-speaker panel the
          full-screen variant carries. */}
      {contained && (voice.status === 'live' || voice.status === 'connecting' || micDenied) && (
        <>
          <div style={{ flex: 1 }} />
          {/* Info — a small popover with the voice preview note; portalled to <body> (below) so the
              contained surface's overflow:hidden never clips it. */}
          <div style={{ position: 'relative', display: 'inline-flex' }} ref={infoTriggerRef}>
            <IconButton
              size="small"
              variant="text"
              // Visible hover on the gray Speak-mode surface (voiceHeaderButton.module.css — the DS ghost
              // hover is invisible here); the selected fill while the popover is open is the inline
              // ghost/active below (surface-agnostic).
              className={voiceHeaderBtn.btn}
              style={infoOpen ? { background: 'var(--ds-fill-ghost-active)' } : undefined}
              aria-label={t('settings.models.voice.title')}
              aria-expanded={infoOpen}
              onClick={() => {
                logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_info', location: 'voice_call' });
                setInfoOpen((o) => !o);
              }}
            >
              <Info size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            </IconButton>
            {infoOpen &&
              infoPos &&
              createPortal(
                <>
                  <div onClick={() => setInfoOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000 }} />
                  {/* "About Voice mode" popover — Figma 14626:190420. Sunken card, 16 radius, 12/16
                      padding, XL shadow; title + body + Continue (outlined) / Customize voice (black).
                      Dropped below the header; CENTERED over the panel on the narrow surfaces
                      (Figma 14626:189496 / 14701:107274 — `left` is the clamped panel center, so
                      `translateX(-50%)` lands it on centre), RIGHT-ALIGNED to the info button in
                      fullscreen (Figma 14626:190539 — `right` off the trigger). Either way it can't run
                      off the window. */}
                  <Card
                    variant="sunken"
                    disablePadding
                    style={{
                      position: 'fixed',
                      top: infoPos.top,
                      ...(infoPos.left !== undefined
                        ? { left: infoPos.left, transform: 'translateX(-50%)' }
                        : { right: infoPos.right }),
                      width: INFO_CARD_WIDTH,
                      zIndex: 1001,
                      padding: '12px 16px',
                      borderRadius: 16,
                      boxShadow:
                        '0 20px 30px rgba(var(--ds-neutral-950-rgb), 0.1), 0 0 2px rgba(var(--ds-neutral-950-rgb), 0.1)',
                    }}
                  >
                    <p style={{ margin: '0 0 12px', fontSize: 16, lineHeight: '20px', fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.02em', color: 'var(--sym-ink)' }}>
                      {t('voiceCall.about.title')}
                    </p>
                    <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: '18px', letterSpacing: '-0.02em', color: 'var(--sym-ink)' }}>
                      {t('voiceCall.about.body', { productName })}
                    </p>
                    <Button variant="outlined" size="medium" fullWidth onClick={() => setInfoOpen(false)} style={{ marginBottom: 8 }}>
                      {t('voiceCall.about.continue')}
                    </Button>
                    <Button
                      variant="contained"
                      color="standard"
                      size="medium"
                      fullWidth
                      href="/agents/personality"
                      onClick={() => {
                        setInfoOpen(false);
                        // Keep the call alive as the "Back to call" island so navigating away doesn't
                        // strand a running-but-invisible call; on mobile also close the full-screen chat
                        // so the Personality page is what the user lands on.
                        voice.minimize();
                        // A full-screen chat (mobile, or desktop fullscreen) covers the page, so close it
                        // to reveal the Personality page; a docked chat can stay.
                        if (narrow || isFullscreen) setChatMode('bubble');
                      }}
                    >
                      {t('voiceCall.about.customize')}
                    </Button>
                  </Card>
                </>,
                document.body,
              )}
          </div>
          <VoiceAudioSettings
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            inputLevel={() => voice.getAudioState().inputLevel}
            onVolume={voice.setVolume}
            onInputDevice={voice.setInputDevice}
            onOutputDevice={voice.setOutputDevice}
            onTestSpeaker={voice.testSpeaker}
            triggerSize="small"
            // Positioned exactly like the "About Voice mode" info card: centered over the header/panel on
            // the narrow surfaces (side-chat + mobile-web) so it can't run off the docked chat's edge,
            // right-aligned to the trigger only in fullscreen.
            panelAlign={isFullscreen ? 'end' : 'center'}
            placement="below"
            // Header-scoped ink hover (gray Speak-mode surface); the selected fill is inline in
            // VoiceAudioSettings so it stays on the composer/bottom trigger too without this hover.
            triggerClassName={voiceHeaderBtn.btn}
            // Drop from the header's bottom (+ the same gap as the info card), not the settings button,
            // so both popovers sit the same distance below the chat header.
            blockAnchorRef={topBarRef}
            blockAnchorGap={INFO_CARD_GAP}
          />
          {/* Expand ↗ — grow the chat surface to fullscreen (wide only; a phone chat is already full). */}
          {!narrow && (
            <IconButton
              size="small"
              variant="text"
              // Same visible-on-gray hover as the info/settings buttons beside it (no popover, so no
              // aria-expanded selected state — just hover).
              className={voiceHeaderBtn.btn}
              aria-label={isFullscreen ? t('chat.float') : t('chat.expand')}
              onClick={() => {
                logPageClickBi({ ...CHAT_HOME_BI, button_name: isFullscreen ? 'voice_shrink' : 'voice_expand', location: 'voice_call' });
                setChatMode(isFullscreen ? 'expanded' : 'fullscreen');
              }}
            >
              {isFullscreen ? (
                <Minimize2 size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              ) : (
                <Maximize2 size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              )}
            </IconButton>
          )}
        </>
      )}
    </div>
  );

  const orbBlock = (
    <div style={{ position: 'relative', width: haloSize, height: haloSize }}>
      {/* The two halo layers behind the orb — same frame size (Figma/native's 192:88 ratio),
          differentiated by scale + opacity only, both driven every frame by the tick loop above.
          Initial inline values match that formula's resting state (breathe≈0, level≈0) so there's
          no flash of a different look before the first frame runs. */}
      <div
        ref={outerHaloRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          width: haloSize,
          height: haloSize,
          borderRadius: '50%',
          background: 'rgba(var(--ds-lime-500-rgb), 0.035)',
          transition: 'transform 80ms linear',
        }}
      />
      <div
        ref={midHaloRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          width: haloSize,
          height: haloSize,
          borderRadius: '50%',
          background: 'rgba(var(--ds-lime-500-rgb), 0.165)',
          transition: 'transform 80ms linear',
        }}
      />
      {/* Wait-ring: traces the ~11s ask_orion wait around the orb (see the
          rAF loop). Hidden until the assistant is working. */}
      <svg
        ref={ringWrapRef}
        width={ringSize}
        height={ringSize}
        viewBox={`0 0 ${ringSize} ${ringSize}`}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          opacity: 0,
          transform: 'rotate(-90deg)',
          pointerEvents: 'none',
        }}
      >
        <circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={ringR}
          fill="none"
          stroke="rgba(var(--hairline-rgb),0.10)"
          strokeWidth={3}
        />
        <circle
          ref={ringRef}
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={ringR}
          fill="none"
          stroke={copy.ringColor}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={ringC}
          strokeDashoffset={ringC}
        />
      </svg>
      <div
        ref={orbRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          width: orbSize,
          height: orbSize,
          borderRadius: '50%',
          background: copy.orbFill,
          boxShadow: 'none',
          transition: 'transform 80ms linear',
        }}
      />
    </div>
  );

  // The default (non-error) status line + live caption. Identical in both presentations — only
  // the error state's presentation differs (see `containedErrorLine` below).
  const statusAndCaption = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, width: '100%' }}>
      <div
        ref={statusRef}
        style={{
          fontSize: 16,
          lineHeight: '24px',
          letterSpacing: '-0.02em',
          color: failed ? 'var(--ds-text-destructive)' : 'var(--ds-text-secondary)',
          minHeight: 24,
          textAlign: 'center',
        }}
      >
        {failed ? errorText : copy.calling}
      </div>

      {/* ScrollArea, not a div with `overflow: auto` — it is the app's only scroll container and the
          only thing that draws a scrollbar (see orion-web CLAUDE.md; `scrollbar-spec.test.ts` fails the
          build on a hand-rolled one). It forwards the ref the end-pin below needs. */}
      <ScrollArea
        ref={captionRef}
        // A live caption is whatever language the call is being held in, so it takes its direction from
        // its OWN script and not the page's — and from `caption.text` alone, because the "You: "/agent
        // prefix is our own copy and letting it vote would drag a short Hebrew caption back to LTR.
        // `messageDir` is the MAJORITY-script rule; `dir="auto"` would be first-strong-char, which lays a
        // mostly-Hebrew line that opens with a Latin word out LTR and puts its trailing "!" on the wrong
        // side. Same rule and same helper shape as iOS/Android (Golden Rule 13).
        dir={voice.caption ? messageDir(voice.caption.text) : undefined}
        style={{
          // A floor, never a ceiling on the text: the area reserves room so the controls below don't
          // jump as the first words land, and then GROWS with the caption. Past `maxHeight` it scrolls
          // (pinned to the end, below) instead of clipping the tail or pushing the controls off — the
          // same floor/ceiling/anchored-to-the-end shape as iOS `VoiceCaptionView` and Android
          // `VoiceCaptionLine`. ScrollArea supplies the overflow itself.
          minHeight: 24,
          maxHeight: 132,
          maxWidth: 'min(85vw, 640px)',
          textAlign: 'center',
          fontSize: 16,
          lineHeight: '24px',
          letterSpacing: '-0.02em',
          color: 'var(--ds-text-primary)',
        }}
      >
        {voice.caption?.text}
      </ScrollArea>
    </div>
  );

  // Contained's decluttered error treatment (Figma 13124:77025): one line, an alert glyph +
  // the same localized copy the full-screen presentation shows in `statusAndCaption` above — no
  // separate caption area, since there is nothing to caption while the call never connected.
  const containedErrorLine = (
    // An error-tinted chip (light destructive surface + destructive text/icon), not bare red text on the
    // gray Speak-mode surface — where --ds-text-destructive only reached ~3.3:1. The light destructive
    // fill lifts the red text to a readable contrast and reads as a standard error. Both tokens are
    // theme-aware.
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          gap: 8,
          maxWidth: '100%',
          padding: '10px 14px',
          borderRadius: 'var(--ds-radius-12)',
          background: 'var(--ds-fill-destructive-secondary-hover)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', height: 20, flexShrink: 0 }}>
          <CircleAlert size={16} aria-hidden style={{ color: 'var(--ds-text-destructive)' }} />
        </span>
        <span style={{ fontSize: 14, lineHeight: '20px', letterSpacing: '-0.28px', color: 'var(--ds-text-destructive)', textAlign: 'start' }}>
          {errorText}
        </span>
      </div>
    </div>
  );

  // The mic has been flat for seconds and the user isn't muted: the call is almost certainly
  // deaf. Every cause — the browser opening the wrong input, a hardware mute switch, an
  // OS-denied mic, a virtual device routed elsewhere — looks identical from here, and none
  // of them show up in the transcript, so the only honest move is to say what we observe and
  // hand over the control that fixes it.
  const silenceHint = voice.status === 'live' && voice.micSilent && !muted && (
    <div style={SILENCE_HINT} role="status">
      <MicOff size={16} aria-hidden style={{ color: 'var(--ds-control-fill-on-default)', flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{t('voiceCall.micSilent')}</span>
      <Button
        variant="outlined"
        size="tiny"
        onClick={() => {
          logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_mic_silent_fix', location: 'voice_call' });
          setSettingsOpen(true);
        }}
      >
        {t('voiceCall.micSilentAction')}
      </Button>
    </div>
  );

  // The call is approaching the server's cap. Said plainly, and only in the last two minutes:
  // the alternative — what shipped before — was the call simply ending, which reads as a crash
  // rather than a limit. Never counts down to "0 min" (see endingSoonMinutes).
  const endingSoonHint = voice.timing.endingSoonMinutes != null && (
    <div style={SILENCE_HINT} role="status" data-testid="voice-call-ending-soon">
      <span style={{ flex: 1 }}>{t('voiceCall.endingSoon', { minutes: voice.timing.endingSoonMinutes })}</span>
    </div>
  );

  // A single labeled circular call control (Figma 12835:79206): a 48px round DS Icon Button with
  // its caption beneath. The DS Icon Button tops out at 42px/radius-8, so the 48px round geometry is
  // an inline override on the DS component (never a hand-rolled button element — see CLAUDE.md "Buttons").
  const callControl = (
    key: string,
    label: string,
    icon: React.ReactNode,
    ariaLabel: string,
    onClick: () => void,
    variant: 'contained' | 'outlined',
    color: 'standard' | 'destructive',
    extraStyle?: React.CSSProperties,
  ) => (
    <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <IconButton
        variant={variant}
        color={color}
        size="large"
        style={{ width: 48, height: 48, borderRadius: '50%', ...extraStyle }}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {icon}
      </IconButton>
      <span
        style={{ fontSize: 12, fontWeight: 'var(--sym-weight-semibold)', lineHeight: '16px', letterSpacing: '-0.24px', color: 'var(--ds-text-primary)' }}
      >
        {label}
      </span>
    </div>
  );

  // Native full-surface controls: Mute at bottom-start, End at bottom-end (Figma 12835:79206).
  // Both are shown for the whole active call. Mute is Figma's Fill/Light circle throughout — the DS
  // `outlined` skin is transparent-filled, which read as an invisible ring on the surface, so it
  // carries an explicit fill: WHITE (Fill/Light default) with `Mic` when live, and the pressed
  // Fill/Light Active grey (#DBDBDB = --ds-neutral-200) with `MicOff` while muted (Figma 14701:106791) —
  // NOT the black `contained` skin. Capped width + centered so the two don't fly to opposite corners.
  const bottomControls = (active || micDenied) && (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: 320,
        margin: '0 auto',
        paddingBottom: 4,
      }}
    >
      {callControl(
        'mute',
        copy.muteButton,
        muted ? <MicOff size={22} aria-hidden /> : <Mic size={22} aria-hidden />,
        muted ? copy.unmuteAria : copy.muteButton,
        () => {
          logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_mute_toggle', location: 'voice_call' });
          voice.toggleMuted();
        },
        'outlined',
        'standard',
        { background: muted ? 'var(--surface-press)' : 'var(--ds-surface-sunken)', borderColor: 'transparent' },
      )}
      {callControl(
        'end',
        copy.end,
        <X size={22} aria-hidden />,
        copy.endCall,
        () => {
          logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_end_call', location: 'voice_call' });
          voice.end();
        },
        'contained',
        'destructive',
      )}
    </div>
  );

  // A failed call never went live, so there's nothing to mute or hang up — just one Dismiss control,
  // centered, that owns the only teardown (the black skin reads "close this", not "destroy").
  const dismissControl = failed && (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', paddingBottom: 4 }}>
      {callControl(
        'dismiss',
        copy.dismiss,
        <X size={22} aria-hidden />,
        copy.dismiss,
        () => {
          logPageClickBi({ ...CHAT_HOME_BI, button_name: 'voice_dismiss_error', location: 'voice_call' });
          voice.end();
        },
        'contained',
        'standard',
      )}
    </div>
  );

  if (contained) {
    // The composer is hidden while this is up (MessagesPanel gates it on surfaceVisible), so the call
    // surface owns the whole panel like the native full-surface presentation: top bar, centered orb +
    // caption, and the labeled Mute/End controls pinned to the bottom (Figma 12835:79206).
    return (
      <>
        {topBar}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            width: '100%',
            boxSizing: 'border-box',
            color: 'rgba(var(--ink-rgb), 0.95)',
            // The whole "Speak Mode" surface is the Figma light-gray (Surface/Light Gray #eae9e9),
            // which is also what makes the white Mute circle read.
            background: 'var(--ds-surface-light-gray)',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
            }}
          >
            {/* Orb and the status/caption sit 56 apart (Figma "Maestro speaking" 14701:106994 /
                "Mic- no access" 14511:136726 — both `gap-56`); the two text lines are 12 apart,
                owned by `statusAndCaption`. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 56, width: '100%' }}>
              {orbBlock}
              {failed ? containedErrorLine : statusAndCaption}
            </div>
            {silenceHint}
            {endingSoonHint}
          </div>
          {failed && !micDenied ? dismissControl : bottomControls}
        </div>
      </>
    );
  }

  const fullContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        padding: 24,
        background: 'rgba(var(--surface-base-rgb), 0.94)',
        // Portal lifts us out of <main>, which is where the Orion font stack
        // lives. Restate it here so the call surface doesn't fall back to
        // the browser's default serif.
        color: 'rgba(var(--ink-rgb), 0.95)',
        fontFamily: 'var(--brand-font-ui, ui-sans-serif, -apple-system, "SF Pro Display", system-ui, sans-serif)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      {topBar}
      {orbBlock}
      {statusAndCaption}
      {silenceHint}
      {endingSoonHint}

      {!failed && (
        <div
          aria-label={t('voiceCall.micLevel')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            height: 48,
          }}
        >
          {Array.from({ length: 9 }, (_, i) => (
            <div
              key={i}
              ref={(el) => {
                barRefs.current[i] = el;
              }}
              style={{
                width: 4,
                height: 44,
                borderRadius: 2,
                // Control ink, not the brand lime. These bars are the only signal that the mic is
                // picking anything up, so they have to be readable — and #A4FF70 on white is not.
                // --ds-control-fill-on-default is the token for a control in its "on" state and flips
                // to light ink in dark mode on its own.
                background: 'var(--ds-control-fill-on-default)',
                opacity: 0.55,
                transformOrigin: 'bottom',
              }}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        {(voice.status === 'live' || voice.status === 'connecting') && (
          <VoiceAudioSettings
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            inputLevel={() => voice.getAudioState().inputLevel}
            onVolume={voice.setVolume}
            onInputDevice={voice.setInputDevice}
            onOutputDevice={voice.setOutputDevice}
            onTestSpeaker={voice.testSpeaker}
          />
        )}
        {voice.status === 'live' && (
          <button
            type="button"
            onClick={voice.toggleMuted}
            style={{
              padding: '10px 22px',
              borderRadius: 999,
              border: '1px solid rgba(var(--hairline-rgb),0.18)',
              background: voice.muted ? 'rgba(var(--ds-lime-200-rgb), 0.18)' : 'rgba(var(--hairline-rgb),0.06)',
              color: 'rgba(var(--ink-rgb),0.85)',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {voice.muted ? t('voiceCall.unmute') : t('voiceCall.mute')}
          </button>
        )}
        <button
          type="button"
          onClick={voice.end}
          style={{
            padding: '10px 26px',
            borderRadius: 999,
            border: 'none',
            backgroundColor: failed ? 'rgba(var(--hairline-rgb),0.1)' : 'rgba(var(--ds-fill-destructive-default-rgb), 0.85)',
            color: failed ? 'rgba(var(--hairline-rgb),0.85)' : 'var(--ds-red-950)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 'var(--sym-weight-semibold)',
          }}
        >
          {failed ? t('voiceCall.dismiss') : t('voiceCall.endCall')}
        </button>
      </div>

      {/* Honesty footer. Voice is an early preview and the live model can be
          wrong — set expectations without over-promising (the safer framing,
          since a fabricated turn leaves nothing in chat to "check"). */}
      {!failed && (
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: 24,
            right: 24,
            textAlign: 'center',
            fontSize: 12,
            lineHeight: 1.5,
            color: 'rgba(var(--ink-rgb), 0.4)',
          }}
        >
          {t('voiceCall.previewDisclaimer', { productName })}
        </div>
      )}
    </div>
  );

  return createPortal(fullContent, portalTarget as HTMLElement);
}
